/**
 * Small pieces every Missions view shares: states as icon plus label (plain in rows, a soft
 * pill only in headers), kind and priority marks, the progress bar. Colour follows
 * docs/design-system/language/status.md: cyan while something runs, amber when it waits on a
 * person, red when it failed, grey for everything else (done included).
 */
import {
  Boxes,
  Bug,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleMinus,
  CircleStop,
  CircleX,
  Eye,
  FlaskConical,
  Hand,
  Layers,
  ShieldAlert,
  ListChecks,
  LoaderCircle,
  CirclePause,
  SignalHigh,
  SignalLow,
  SignalMedium,
  SquareCheck,
  Wrench,
  Circle,
} from '@orgus/ui/icons';
import type { ComponentType } from 'react';
import { CANONICAL_STATE_LABEL, ITEM_KIND_LABEL, ITEM_PRIORITY_LABEL, ITEM_STATUS_LABEL, MISSION_STATE_LABEL, statusLabel } from '@orgus/protocol';
import type { Blocker, CanonicalSessionState, ItemKind, ItemPriority, ItemStatus, MissionState } from '@orgus/protocol';
import { Gauge, Tooltip, cn } from '@orgus/ui';
import type { Tone } from '@orgus/ui';

type Icon = ComponentType<{ className?: string }>;

/** The five visual tones of status.md. */
export type StateTone = 'attention' | 'danger' | 'working' | 'success' | 'neutral';

const TONE_FG: Record<StateTone, string> = {
  attention: 'text-status-attention',
  danger: 'text-status-danger',
  working: 'text-status-working',
  success: 'text-status-success',
  neutral: 'text-fg-subtle',
};
const TONE_SOFT: Record<StateTone, string> = {
  attention: 'bg-status-attention-soft text-status-attention-ink',
  danger: 'bg-status-danger-soft text-status-danger-ink',
  working: 'bg-status-working-soft text-status-working-ink',
  success: 'bg-status-success-soft text-status-success-ink',
  neutral: 'bg-hover text-fg-muted',
};

/**
 * One state: icon plus label. Plain (no fill) in rows and cards; soft (a tinted pill) in drawer
 * and page headers, and in a row only for attention and danger.
 */
export function StatePill({ tone, icon: I, label, variant = 'plain', spin, iconOnly, className }: { tone: StateTone; icon: Icon; label: string; variant?: 'plain' | 'soft'; spin?: boolean; iconOnly?: boolean; className?: string }) {
  const icon = (
    <span className={cn('inline-flex shrink-0', variant === 'plain' || tone === 'neutral' ? TONE_FG[tone] : undefined)}>
      <I className={cn(variant === 'soft' ? 'size-3' : 'size-4', spin && 'motion-safe:animate-spin')} />
    </span>
  );
  if (iconOnly) {
    return (
      <Tooltip content={label}>
        <span role="img" aria-label={label} tabIndex={0} className={cn('inline-flex shrink-0', className)} data-status-pill data-variant="plain" data-tone={tone}>
          {icon}
        </span>
      </Tooltip>
    );
  }
  return (
    <span
      data-status-pill
      data-variant={variant}
      data-tone={tone}
      className={cn(
        'inline-flex min-w-0 shrink-0 items-center whitespace-nowrap',
        variant === 'soft' ? cn('h-(--pill-height) gap-(--pill-gap) rounded-pill px-(--pill-padding-x) text-caption font-medium', TONE_SOFT[tone]) : cn('gap-2 text-ui', tone === 'attention' || tone === 'danger' ? cn('font-medium', TONE_FG[tone]) : tone === 'working' ? TONE_FG[tone] : 'text-fg-muted'),
        className,
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
    </span>
  );
}

const MISSION_ICON: Record<MissionState, Icon> = { planned: CircleDashed, running: LoaderCircle, waiting: Hand, done: CircleCheck, failed: CircleX, stopped: CircleStop };
const MISSION_TONE: Record<MissionState, StateTone> = { planned: 'neutral', running: 'working', waiting: 'attention', done: 'neutral', failed: 'danger', stopped: 'neutral' };

export function missionStateTone(state: MissionState): StateTone {
  return MISSION_TONE[state];
}

/**
 * A mission's one status. Pass its view's blockers: a mission an expired or denied request stopped
 * reads "Blocked" (red, the shield), never "Waiting for you", so the pill and its caption agree.
 */
export function MissionStatePill({ state, blockers, variant = 'plain', className }: { state: MissionState; blockers?: readonly Blocker[]; variant?: 'plain' | 'soft'; className?: string }) {
  if (state === 'waiting' && blockers?.some((b) => b.kind === 'approval' && b.outcome && b.phase !== 'resuming')) {
    return <StatePill tone="danger" icon={ShieldAlert} label="Blocked" variant={variant} className={className} />;
  }
  // "Needs you" is the one label for work waiting on a person (content/needs-you-noun).
  return <StatePill tone={MISSION_TONE[state]} icon={MISSION_ICON[state]} label={state === 'waiting' ? 'Needs you' : MISSION_STATE_LABEL[state]} variant={variant} spin={state === 'running'} className={className} />;
}

const SESSION_ICON: Record<CanonicalSessionState, Icon> = {
  starting: CircleDashed,
  working: LoaderCircle,
  waiting_for_human: Hand,
  idle: CirclePause,
  done: CircleCheck,
  failed: CircleX,
  stopped: CircleStop,
};
const SESSION_TONE: Record<CanonicalSessionState, StateTone> = { starting: 'neutral', working: 'working', waiting_for_human: 'attention', idle: 'neutral', done: 'neutral', failed: 'danger', stopped: 'neutral' };

export function SessionStatePill({ state, className }: { state: CanonicalSessionState; className?: string }) {
  return <StatePill tone={SESSION_TONE[state]} icon={SESSION_ICON[state]} label={CANONICAL_STATE_LABEL[state]} spin={state === 'working'} className={className} />;
}

const ITEM_STATUS_ICON: Record<ItemStatus, Icon> = {
  triage: CircleDot,
  backlog: CircleDashed,
  ready: Circle,
  in_progress: LoaderCircle,
  review: Eye,
  done: CircleCheck,
  dropped: CircleMinus,
};

/** Only work in progress is coloured; every other item status is grey and told apart by its icon. */
export function itemStateTone(status: ItemStatus): StateTone {
  return status === 'in_progress' ? 'working' : 'neutral';
}

/** An item status as icon plus label (the label reads per kind: a defect's done is Fixed). */
export function ItemState({ status, kind, variant = 'plain', iconOnly, className }: { status: ItemStatus; kind?: ItemKind; variant?: 'plain' | 'soft'; iconOnly?: boolean; className?: string }) {
  return <StatePill tone={itemStateTone(status)} icon={ITEM_STATUS_ICON[status]} label={kind ? statusLabel(kind, status) : ITEM_STATUS_LABEL[status]} variant={variant} iconOnly={iconOnly} spin={status === 'in_progress'} className={className} />;
}

export function ItemStatusPill({ status, kind, className }: { status: ItemStatus; kind?: ItemKind; className?: string }) {
  return <ItemState status={status} kind={kind} variant="soft" className={className} />;
}

/** The status icon alone, for dense places that already say the status in words nearby. */
export function ItemStatusIcon({ status, className }: { status: ItemStatus; className?: string }) {
  const I = ITEM_STATUS_ICON[status];
  return (
    <span role="img" aria-label={ITEM_STATUS_LABEL[status]} className={cn('inline-flex shrink-0', TONE_FG[itemStateTone(status)], className)}>
      <I className={cn('size-4', status === 'in_progress' && 'motion-safe:animate-spin')} />
    </span>
  );
}

const KIND_ICON: Record<ItemKind, Icon> = { epic: Layers, feature: Boxes, story: ListChecks, task: SquareCheck, defect: Bug, spike: FlaskConical, chore: Wrench };

/** The kind of an item: a grey icon, named by its tooltip and accessible label. */
export function KindIcon({ kind, className, tooltip = true }: { kind: ItemKind; className?: string; tooltip?: boolean }) {
  const I = KIND_ICON[kind];
  const mark = (
    <span role="img" aria-label={ITEM_KIND_LABEL[kind]} tabIndex={tooltip ? -1 : undefined} className={cn('inline-flex shrink-0 text-fg-subtle', className)}>
      <I className="size-4" />
    </span>
  );
  return tooltip ? <Tooltip content={ITEM_KIND_LABEL[kind]}>{mark}</Tooltip> : mark;
}

const PRIORITY_ICON: Record<ItemPriority, Icon> = { 0: CircleAlert, 1: SignalHigh, 2: SignalMedium, 3: SignalLow };

/** Priority: a grey mark (urgent has its own shape, not a colour), with the word where there is room. */
export function PriorityMark({ priority, withLabel, className }: { priority: ItemPriority; withLabel?: boolean; className?: string }) {
  const I = PRIORITY_ICON[priority];
  const label = ITEM_PRIORITY_LABEL[priority];
  const mark = (
    <span role="img" aria-label={`${label} priority`} className={cn('inline-flex shrink-0 items-center gap-2 text-ui', priority === 0 ? 'text-fg' : 'text-fg-subtle', className)}>
      <I className="size-4" />
      {withLabel && <span className="text-fg-muted">{label}</span>}
    </span>
  );
  return withLabel ? mark : <Tooltip content={`${label} priority`}>{mark}</Tooltip>;
}

/** The one progress bar of a mission or epic: a meter plus the sentence that explains it. */
export function ProgressBar({ value, label, tone, caption, className }: { value: number; label: string; tone?: Tone; caption?: string; className?: string }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      <Gauge value={value} label={label} tone={tone ?? 'accent'} showValue={false} />
      {caption && <span className="truncate text-caption text-fg-muted">{caption}</span>}
    </div>
  );
}

